class YGIdeaStarterResources extends HTMLElement {
  constructor() {
    super();

    this.handleClick = this.handleClick.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.renderHits = this.renderHits.bind(this);

    this.search = null;
    this.pageCache = new Map();
    this.allCards = [];
    this.currentCards = [];
    this.currentPageCards = [];
    this.currentPage = 1;
    this.renderRequestId = 0;
    this.sortMode = 'relevant';
    this.filtersOpen = true;
    this.gridScale = 3;
    this.spaceTypeSelections = new Set();
    this.showAllSpaceTypes = false;
    this.selectedFiles = new Map();
    this.zipLibLoaded = false;
    this.zipLib = null;
    this.applyInitialUrlFilters();
  }

  connectedCallback() {
    this.cacheElements();
    this.bindEvents();
    this.setFiltersOpen(this.isMobileLayout() ? false : this.dataset.filtersOpen !== 'false');
    this.dataset.filtersInitialized = 'true';
    this.loadDependencies()
      .then(() => this.setupSearch())
      .catch((error) => {
        console.error('Failed to initialize idea starter resources.', error);
        this.renderErrorState();
      });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('change', this.handleChange);
    document.removeEventListener('click', this.handleDocumentClick);

    if (this.search) {
      this.search.dispose();
    }
  }

  cacheElements() {
    this.searchBoxContainer = this.querySelector('[data-search-box]');
    this.cardsContainer = this.querySelector('[data-cards]');
    this.paginationContainer = this.querySelector('[data-pagination]');
    this.resultsCountElement = this.querySelector('[data-results-count]');
    this.spaceTypeRefinement = this.querySelector('[data-space-type-refinement]');
    this.filters = this.querySelector('[data-filters]');
    this.filtersToggleLabels = this.querySelectorAll('[data-filters-toggle-label]');
    this.floatPanel = this.querySelector('[data-float-panel]');
    this.floatDownloadButton = this.querySelector('[data-float-download-trigger]');
    this.floatSelectAll = this.querySelector('[data-float-select-all-trigger]');
    this.gridScaleSlider = this.querySelector('[data-grid-scale-slider]');
    this.multiSelectToggle = this.querySelector('[data-multi-select-trigger]');
  }

  bindEvents() {
    this.addEventListener('click', this.handleClick);
    this.addEventListener('change', this.handleChange);
    document.addEventListener('click', this.handleDocumentClick);
  }

  loadDependencies() {
    const dependencies = [
      this.loadScript(
        'algoliasearch-lite',
        'https://cdn.jsdelivr.net/npm/algoliasearch@5.53.0/dist/lite/builds/browser.umd.js'
      ),
      this.loadScript(
        'instantsearch',
        'https://cdn.jsdelivr.net/npm/instantsearch.js@4.79.2/dist/instantsearch.production.min.js'
      )
    ];

    return Promise.all(dependencies);
  }

  loadScript(key, src) {
    return new Promise((resolve, reject) => {
      const globalReady = key === 'instantsearch'
        ? Boolean(window.instantsearch)
        : Boolean(this.getAlgoliaSearchFactory());

      if (globalReady) {
        resolve();
        return;
      }

      const existingScript = document.querySelector(`script[data-yg-script="${key}"]`);

      if (existingScript) {
        existingScript.addEventListener('load', resolve, { once: true });
        existingScript.addEventListener('error', () => reject(new Error(`${key} failed to load`)), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.defer = true;
      script.dataset.ygScript = key;
      script.onload = resolve;
      script.onerror = () => reject(new Error(`${key} failed to load`));
      document.head.appendChild(script);
    });
  }

  getAlgoliaSearchFactory() {
    const algoliaLite = window['algoliasearch/lite'];

    if (typeof window.algoliasearch === 'function') {
      return window.algoliasearch;
    }

    if (algoliaLite && typeof algoliaLite.liteClient === 'function') {
      return algoliaLite.liteClient;
    }

    if (typeof algoliaLite === 'function') {
      return algoliaLite;
    }

    return null;
  }

  applyInitialUrlFilters() {
    const params = new URLSearchParams(window.location.search);
    const spaceTypeValues = [
      params.get('space_type'),
      params.get('space-type')
    ].filter(Boolean);

    spaceTypeValues.forEach((value) => {
      decodeURIComponent(value)
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
        .forEach((item) => this.spaceTypeSelections.add(item));
    });
  }

  setupSearch() {
    const algoliaSearch = this.getAlgoliaSearchFactory();
    const instantsearch = window.instantsearch;

    if (!algoliaSearch || !instantsearch) {
      throw new Error('Algolia dependencies are not available.');
    }

    const searchClient = algoliaSearch(this.algoliaAppId, this.algoliaSearchKey);
    const widgets = instantsearch.widgets;
    const connectHits = instantsearch.connectors.connectHits;

    this.search = instantsearch({
      indexName: this.algoliaIndexName,
      searchClient
    });

    this.search.addWidgets([
      widgets.configure({
        hitsPerPage: this.algoliaHitsPerPage,
        attributesToRetrieve: [
          'objectID',
          'id',
          'title',
          'handle',
          'meta'
        ]
      }),
      widgets.searchBox({
        container: this.searchBoxContainer,
        placeholder: this.searchPlaceholder,
        showSubmit: false,
        showReset: true,
        showLoadingIndicator: false
      }),
      connectHits(this.renderHits)({})
    ]);

    this.search.start();
    this.setFiltersOpen(this.isMobileLayout() ? false : this.dataset.filtersOpen !== 'false');
    this.dataset.filtersInitialized = 'true';
    this.setGridScale(this.gridScaleSlider?.value || this.gridScale);
    this.toggleMultiSelectMode(false);
  }

  async renderHits(renderOptions) {
    const { hits, results } = renderOptions;

    if (!results) {
      return;
    }

    const requestId = ++this.renderRequestId;
    const pageReferences = this.getUniquePageReferences(hits);

    this.currentPage = 1;

    if (pageReferences.length === 0) {
      this.allCards = [];
      this.applyFilters();
      return;
    }

    this.renderLoadingState();

    try {
      const pages = await this.ensurePages(pageReferences);

      if (requestId !== this.renderRequestId) {
        return;
      }

      this.allCards = this.buildCards(hits, pages);
      this.applyFilters();
    } catch (error) {
      if (requestId !== this.renderRequestId) {
        return;
      }

      console.error('Failed to render idea starter resources.', error);
      this.renderErrorState();
    }
  }

  getUniquePageReferences(hits) {
    const seen = new Set();

    return (hits || []).reduce((accumulator, hit) => {
      if (!this.hasIdeaStarterTitle(hit)) {
        return accumulator;
      }

      const pageId = this.getPageIdFromHit(hit);

      if (!pageId || seen.has(pageId)) {
        return accumulator;
      }

      seen.add(pageId);
      accumulator.push({
        id: pageId,
        hit
      });
      return accumulator;
    }, []);
  }

  getPageIdFromHit(hit) {
    const rawId = hit?.id || hit?.objectID || '';
    const id = String(rawId).trim();

    if (!id) {
      return '';
    }

    return id.includes('gid://shopify/Page/') ? id : `gid://shopify/Page/${this.extractNumericId(id)}`;
  }

  async ensurePages(pageReferences) {
    const missingReferences = pageReferences.filter((pageReference) => !this.pageCache.has(pageReference.id));

    if (missingReferences.length > 0) {
      const batches = this.chunkArray(missingReferences, 20);

      for (const batch of batches) {
        const pages = await this.fetchPages(batch.map((pageReference) => pageReference.id));

        pages.forEach((page) => {
          this.pageCache.set(page.id, page);
        });
      }
    }

    return pageReferences
      .map((pageReference) => this.pageCache.get(pageReference.id))
      .filter(Boolean);
  }

  async fetchPages(ids) {
    const body = {
      query: `
        query getIdeaStarterPages($ids: [ID!]!) {
          nodes(ids: $ids) {
            ... on Page {
              id
              title
              handle
              metafield(namespace: "custom", key: "yg_idea_starter") {
                value
                reference {
                  ... on Metaobject {
                    id
                    type
                    handle
                    fields {
                      key
                      value
                      reference {
                        __typename
                        ... on MediaImage {
                          image {
                            url
                            altText
                          }
                        }
                        ... on GenericFile {
                          url
                          mimeType
                        }
                      }
                      references(first: 50) {
                        nodes {
                          ... on Metaobject {
                            id
                            type
                            handle
                            fields {
                              key
                              value
                              reference {
                                __typename
                                ... on GenericFile {
                                  url
                                  mimeType
                                }
                                ... on MediaImage {
                                  image {
                                    url
                                    altText
                                  }
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `,
      variables: { ids }
    };

    const response = await fetch(this.storefrontEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.storefrontToken
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      throw new Error(`Storefront API request failed with status ${response.status}`);
    }

    const payload = await response.json();

    if (payload.errors?.length) {
      throw new Error(payload.errors.map((error) => error.message).join(', '));
    }

    return (payload?.data?.nodes || [])
      .filter(Boolean)
      .map((node) => this.normalizePage(node));
  }

  normalizePage(node) {
    const fields = this.fieldsArrayToMap(node?.metafield?.reference?.fields || []);
    const image = fields.image?.reference?.image;
    const ideaTitle = this.normalizeString(fields.title?.value);
    const files = this.getUniqueFiles(this.parseDownloadFiles(fields.yg_idea_starter_files, ideaTitle || node.title));

    return {
      id: node.id,
      numericId: this.extractNumericId(node.id),
      title: this.normalizeString(node.title),
      handle: this.normalizeString(node.handle),
      ideaTitle,
      spaceType: this.normalizeString(fields.space_type?.value),
      imageUrl: image?.url || '',
      imageAlt: image?.altText || ideaTitle || this.normalizeString(node.title),
      files
    };
  }

  parseDownloadFiles(field, fallbackTitle) {
    const nodes = field?.references?.nodes || [];

    return nodes
      .filter((node) => node?.fields)
      .map((node) => {
        const fields = this.fieldsArrayToMap(node.fields);
        const fileField = fields.file_link || fields.file || fields.download_file || fields.file_url;
        const fileUrl = this.getFileUrl(fileField);

        if (!fileUrl) {
          return null;
        }

        const fileTitle = this.normalizeString(fields.title?.value) || fallbackTitle;
        const fileName = this.getFileNameFromUrl(fileUrl) || this.buildFileName(fileTitle, fileUrl);
        const fileType = this.normalizeString(fields.file_type?.value) || this.getActualFileType({
          fileName,
          url: fileUrl,
          mimeType: this.normalizeString(fileField?.reference?.mimeType)
        });

        return {
          title: fileTitle,
          url: fileUrl,
          fileName,
          mimeType: this.normalizeString(fileField?.reference?.mimeType),
          fileSize: this.normalizeString(fields.file_size?.value),
          fileType
        };
      })
      .filter(Boolean);
  }

  buildCards(hits, pages) {
    const pageMap = new Map(pages.map((page) => [page.id, page]));

    return (hits || []).map((hit) => {
      const pageId = this.getPageIdFromHit(hit);
      const page = pageMap.get(pageId);

      if (!page) {
        return null;
      }

      const hitTitle = this.getHitIdeaStarterTitle(hit);
      const title = hitTitle || page.ideaTitle || page.title;

      return {
        id: page.id,
        title,
        pageTitle: page.title,
        handle: page.handle,
        url: page.handle ? `/pages/${page.handle}` : '#',
        spaceType: page.spaceType,
        imageUrl: page.imageUrl,
        imageAlt: page.imageAlt || title,
        files: page.files,
        searchableText: [
          title,
          page.title,
          page.spaceType,
          ...page.files.map((file) => file.title),
          ...page.files.map((file) => file.fileName),
          ...page.files.map((file) => file.fileType)
        ].filter(Boolean).join(' ')
      };
    }).filter(Boolean);
  }

  getHitIdeaStarterTitle(hit) {
    return this.normalizeString(hit?.meta?.custom?.yg_idea_starter_title)
      || this.normalizeString(hit?.meta?.custom?.idea_starter_title);
  }

  hasIdeaStarterTitle(hit) {
    return this.getHitIdeaStarterTitle(hit) !== '';
  }

  applyFilters() {
    this.currentCards = this.allCards.filter((card) => {
      if (this.spaceTypeSelections.size === 0) {
        return true;
      }

      return this.getCardSpaceTypes(card).some((spaceType) => this.spaceTypeSelections.has(spaceType));
    });

    this.sortCards();
    this.renderFacets();
    this.renderCards();
  }

  sortCards() {
    if (this.sortMode === 'relevant') {
      return;
    }

    const direction = this.sortMode === 'name-desc' ? -1 : 1;

    this.currentCards.sort((cardA, cardB) => (
      direction * String(cardA.title || '').localeCompare(String(cardB.title || ''), undefined, { sensitivity: 'base' })
    ));
  }

  renderFacets() {
    if (!this.spaceTypeRefinement) {
      return;
    }

    const items = this.computeSpaceTypeCounts();
    const visibleItems = this.showAllSpaceTypes ? items : items.slice(0, this.facetLimit);

    if (items.length === 0) {
      this.spaceTypeRefinement.innerHTML = `<p class="yg-idea-starter-resources__facet-empty text-sm">${this.escapeHtml(this.noFiltersText)}</p>`;
      return;
    }

    this.spaceTypeRefinement.innerHTML = `
      <ul class="yg-idea-starter-resources__facet-list">
        ${visibleItems.map((item) => this.renderFacetItem(item)).join('')}
      </ul>
      ${items.length > this.facetLimit ? `
        <button type="button" class="yg-idea-starter-resources__show-more text-sm" data-space-type-show-more>
          ${this.escapeHtml(this.showAllSpaceTypes ? this.showLessText : this.showMoreText)}
        </button>
      ` : ''}
    `;
  }

  computeSpaceTypeCounts() {
    const counts = new Map();

    this.allCards.forEach((card) => {
      this.getCardSpaceTypes(card).forEach((spaceType) => {
        counts.set(spaceType, (counts.get(spaceType) || 0) + 1);
      });
    });

    return Array.from(counts.entries())
      .map(([value, count]) => ({ value, count }))
      .sort((itemA, itemB) => itemA.value.localeCompare(itemB.value));
  }

  getCardSpaceTypes(card) {
    return this.normalizeString(card?.spaceType)
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  renderFacetItem(item) {
    const checked = this.spaceTypeSelections.has(item.value) ? ' checked' : '';

    return `
      <li class="yg-idea-starter-resources__facet-item">
        <label class="yg-idea-starter-resources__facet-label">
          <span class="yg-idea-starter-resources__facet-text">${this.escapeHtml(item.value)} (${item.count})</span>
          <input
            type="checkbox"
            class="yg-idea-starter-resources__facet-checkbox"
            data-space-type-checkbox
            value="${this.escapeAttribute(item.value)}"
            ${checked}
          >
          <span class="yg-idea-starter-resources__facet-check"></span>
        </label>
      </li>
    `;
  }

  renderCards() {
    if (!this.cardsContainer) {
      return;
    }

    if (this.currentCards.length === 0) {
      this.cardsContainer.innerHTML = `<div class="yg-idea-starter-resources__empty text-base">${this.escapeHtml(this.noResultsText)}</div>`;
      this.currentPageCards = [];
      this.renderPagination();
      this.updateResultsCount();
      this.updateSelectedCounter();
      return;
    }

    const totalPages = this.getTotalPages();

    if (this.currentPage > totalPages) {
      this.currentPage = totalPages;
    }

    const startIndex = (this.currentPage - 1) * this.pageSize;
    this.currentPageCards = this.currentCards.slice(startIndex, startIndex + this.pageSize);
    this.cardsContainer.innerHTML = this.currentPageCards.map((card, index) => this.renderCard(card, startIndex + index)).join('');

    this.renderPagination();
    this.updateResultsCount();
    this.syncRenderedSelections();
    this.updateSelectedCounter();
  }

  renderCard(card, cardIndex) {
    const cardKey = this.getCardKey(card, cardIndex);
    const imageMarkup = card.imageUrl
      ? `<img src="${this.escapeAttribute(card.imageUrl)}" alt="${this.escapeAttribute(card.imageAlt || card.title)}" class="yg-idea-starter-resources__card-image">`
      : `<div class="yg-idea-starter-resources__card-placeholder"></div>`;

    return `
      <article class="yg-idea-starter-resources__card" data-card-id="${this.escapeAttribute(cardKey)}">
        <div class="yg-idea-starter-resources__card-media">
          <a class="yg-idea-starter-resources__card-image-link" href="${this.escapeAttribute(card.url)}" aria-label="${this.escapeAttribute(card.title)}">
            ${imageMarkup}
          </a>
          ${this.renderCardCheckbox(card, cardKey)}
        </div>
        ${this.renderDownloadButton(card)}
        <h3 class="yg-idea-starter-resources__card-title">
          <a href="${this.escapeAttribute(card.url)}">${this.escapeHtml(card.title)}</a>
        </h3>
      </article>
    `;
  }

  renderCardCheckbox(card, cardKey) {
    const files = this.getCardDownloadFiles(card);

    if (files.length === 0) {
      return '';
    }

    return `
      <label class="yg-idea-starter-resources__card-checkbox" aria-label="Select ${this.escapeAttribute(card.title)}">
        <input
          type="checkbox"
          class="yg-idea-starter-resources__card-checkbox-input"
          data-card-checkbox
          data-file-key="${this.escapeAttribute(cardKey)}"
          data-file-url="${this.escapeAttribute(files[0]?.url || '')}"
          data-file-size="${this.escapeAttribute(files.reduce((total, file) => total + this.getFileSizeBytes(file.fileSize), 0))}"
          data-product-title="${this.escapeAttribute(card.title)}"
          data-file-name="${this.escapeAttribute(files[0]?.fileName || this.buildFileName(card.title, files[0]?.url || ''))}"
          data-card-files="${this.escapeAttribute(JSON.stringify(files.map((file) => ({
            url: file.url,
            size: this.getFileSizeBytes(file.fileSize),
            title: file.title || card.title,
            fileName: file.fileName || this.buildFileName(file.title || card.title, file.url)
          }))))}"
        >
        <span class="yg-idea-starter-resources__card-checkbox-custom"></span>
      </label>
    `;
  }

  renderDownloadButton(card) {
    const files = this.getCardDownloadFiles(card);

    if (files.length === 0) {
      return `
        <span class="yg-idea-starter-resources__download yg-idea-starter-resources__download--disabled text-sm">
          <span>${this.escapeHtml(this.downloadText)}</span>
          ${this.renderChevronIcon()}
        </span>
      `;
    }

    return `
      <div class="yg-idea-starter-resources__download-dropdown" data-download-dropdown>
        <button
          class="yg-idea-starter-resources__download text-sm"
          type="button"
          data-download-toggle
          aria-expanded="false"
        >
          <span>${this.escapeHtml(this.downloadText)}</span>
          ${this.renderChevronIcon()}
        </button>
        <div class="yg-idea-starter-resources__download-menu" data-download-menu hidden>
          ${files.map((file) => `
            <a
              class="yg-idea-starter-resources__download-item text-sm"
              href="${this.escapeAttribute(file.url)}"
              download="${this.escapeAttribute(file.fileName || this.buildFileName(file.title || card.title, file.url))}"
              rel="noopener"
            >
              <span class="yg-idea-starter-resources__download-item-label">
                ${this.renderDownloadItemIcon()}
                <span>${this.escapeHtml(file.title || file.fileType || file.fileName || this.downloadText)}</span>
              </span>
              ${file.fileSize ? `<span class="yg-idea-starter-resources__download-item-type">${this.escapeHtml(file.fileSize)}</span>` : ''}
            </a>
          `).join('')}
        </div>
      </div>
    `;
  }

  renderPagination() {
    if (!this.paginationContainer) {
      return;
    }

    const totalPages = this.getTotalPages();

    if (totalPages <= 1) {
      this.paginationContainer.innerHTML = '';
      return;
    }

    const pages = this.getVisiblePages(totalPages);

    this.paginationContainer.innerHTML = `
      <nav class="yg-idea-starter-resources__pagination collection__pagination pagination" aria-label="Pagination">
        ${this.currentPage === 1
    ? '<span class="pagination__item pagination__item--edge pagination__item--disabled"><span class="pagination__icon pagination__icon--first" aria-hidden="true">&laquo;</span></span>'
    : '<button type="button" class="pagination__item pagination__item--edge group" data-page-action="first" aria-label="First page"><span class="pagination__icon pagination__icon--first" aria-hidden="true">&laquo;</span></button>'}
        ${this.currentPage === 1
    ? '<span class="pagination__item pagination__item--disabled"><span class="pagination__icon" aria-hidden="true">&lsaquo;</span></span>'
    : '<button type="button" class="pagination__item group" data-page-action="previous" aria-label="Previous page"><span class="pagination__icon" aria-hidden="true">&lsaquo;</span></button>'}
        ${pages.map((page) => `
          ${page === this.currentPage
    ? `<span class="pagination__item pagination__page pagination__item--active" aria-current="page" data-pagination-distance="${Math.abs(this.currentPage - page)}">${page}</span>`
    : `<button type="button" class="pagination__item pagination__page group" data-page="${page}" data-pagination-distance="${Math.abs(this.currentPage - page)}" aria-label="Go to page ${page}">${page}</button>`}
        `).join('')}
        ${this.currentPage === totalPages
    ? '<span class="pagination__item pagination__item--disabled"><span class="pagination__icon" aria-hidden="true">&rsaquo;</span></span>'
    : '<button type="button" class="pagination__item group" data-page-action="next" aria-label="Next page"><span class="pagination__icon" aria-hidden="true">&rsaquo;</span></button>'}
        ${this.currentPage === totalPages
    ? '<span class="pagination__item pagination__item--edge pagination__item--disabled"><span class="pagination__icon pagination__icon--last" aria-hidden="true">&raquo;</span></span>'
    : '<button type="button" class="pagination__item pagination__item--edge group" data-page-action="last" aria-label="Last page"><span class="pagination__icon pagination__icon--last" aria-hidden="true">&raquo;</span></button>'}
      </nav>
    `;
  }

  getVisiblePages(totalPages) {
    const start = Math.max(this.currentPage - 2, 1);
    const end = Math.min(this.currentPage + 2, totalPages);
    const pages = [];

    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }

    return pages;
  }

  handleClick(event) {
    if (event.target === this && this.isMobileLayout() && this.filtersOpen) {
      this.setFiltersOpen(false);
      return;
    }

    const filtersClose = event.target.closest('[data-filters-close]');

    if (filtersClose && this.contains(filtersClose)) {
      event.preventDefault();
      this.setFiltersOpen(false);
      return;
    }

    const filtersApply = event.target.closest('[data-filters-apply]');

    if (filtersApply && this.contains(filtersApply)) {
      event.preventDefault();
      this.setFiltersOpen(false);
      this.scrollResultsIntoView();
      return;
    }

    const downloadLink = event.target.closest('.yg-idea-starter-resources__download-item[download]');

    if (downloadLink && this.contains(downloadLink)) {
      this.handleDownloadLink(event, downloadLink);
      return;
    }

    const downloadToggle = event.target.closest('[data-download-toggle]');

    if (downloadToggle && this.contains(downloadToggle)) {
      event.preventDefault();
      this.toggleDownloadDropdown(downloadToggle);
      return;
    }

    const filtersToggle = event.target.closest('[data-filters-toggle]');

    if (filtersToggle && this.contains(filtersToggle)) {
      event.preventDefault();
      this.toggleFilters();
      return;
    }

    const sortToggle = event.target.closest('[data-sort-toggle]');

    if (sortToggle && this.contains(sortToggle)) {
      this.toggleSortDropdown(sortToggle);
      return;
    }

    const sortOption = event.target.closest('[data-sort-option]');

    if (sortOption && this.contains(sortOption)) {
      this.setSortMode(sortOption.dataset.sortOption || 'relevant', sortOption.textContent.trim());
      return;
    }

    const gridScaleMinus = event.target.closest('[data-grid-scale-minus]');

    if (gridScaleMinus && this.contains(gridScaleMinus)) {
      this.setGridScale(this.gridScale - 1);
      return;
    }

    const gridScalePlus = event.target.closest('[data-grid-scale-plus]');

    if (gridScalePlus && this.contains(gridScalePlus)) {
      this.setGridScale(this.gridScale + 1);
      return;
    }

    const facetToggle = event.target.closest('[data-facet-toggle]');

    if (facetToggle && this.contains(facetToggle)) {
      event.preventDefault();
      const section = facetToggle.closest('[data-facet-section]');

      if (!section) {
        return;
      }

      section.dataset.collapsed = section.dataset.collapsed === 'true' ? 'false' : 'true';
      return;
    }

    const showMore = event.target.closest('[data-space-type-show-more]');

    if (showMore && this.contains(showMore)) {
      this.showAllSpaceTypes = !this.showAllSpaceTypes;
      this.renderFacets();
      return;
    }

    const pageButton = event.target.closest('[data-page], [data-page-action]');

    if (pageButton && this.contains(pageButton)) {
      this.handlePageButton(pageButton);
      return;
    }

    const floatDownloadButton = event.target.closest('[data-float-download-trigger]');

    if (floatDownloadButton && this.contains(floatDownloadButton)) {
      event.preventDefault();
      this.startBatchDownload();
      return;
    }

    const floatCloseButton = event.target.closest('[data-float-close-trigger]');

    if (floatCloseButton && this.contains(floatCloseButton)) {
      event.preventDefault();
      this.exitMultiSelectMode();
    }
  }

  handleChange(event) {
    const checkbox = event.target.closest('[data-space-type-checkbox]');

    if (checkbox && this.contains(checkbox)) {
      const value = checkbox.value;

      if (checkbox.checked) {
        this.spaceTypeSelections.add(value);
      } else {
        this.spaceTypeSelections.delete(value);
      }

      this.currentPage = 1;
      this.applyFilters();
      return;
    }

    const cardCheckbox = event.target.closest('[data-card-checkbox]');

    if (cardCheckbox && this.contains(cardCheckbox)) {
      this.handleCardCheckboxChange(cardCheckbox);
      return;
    }

    const gridScaleSlider = event.target.closest('[data-grid-scale-slider]');

    if (gridScaleSlider && this.contains(gridScaleSlider)) {
      this.setGridScale(gridScaleSlider.value);
      return;
    }

    const multiSelectToggle = event.target.closest('[data-multi-select-trigger]');

    if (multiSelectToggle && this.contains(multiSelectToggle)) {
      this.toggleMultiSelectMode(multiSelectToggle.checked);
      return;
    }

    const floatSelectAll = event.target.closest('[data-float-select-all-trigger]');

    if (floatSelectAll && this.contains(floatSelectAll)) {
      this.selectAllCards(floatSelectAll.checked);
    }
  }

  handleDocumentClick(event) {
    if (this.contains(event.target)) {
      return;
    }

    this.closeDropdowns();
    this.closeSortDropdowns();
  }

  handlePageButton(button) {
    const totalPages = this.getTotalPages();
    let nextPage = this.currentPage;

    if (button.dataset.page) {
      nextPage = Number.parseInt(button.dataset.page, 10);
    } else {
      switch (button.dataset.pageAction) {
        case 'first':
          nextPage = 1;
          break;
        case 'previous':
          nextPage = Math.max(this.currentPage - 1, 1);
          break;
        case 'next':
          nextPage = Math.min(this.currentPage + 1, totalPages);
          break;
        case 'last':
          nextPage = totalPages;
          break;
      }
    }

    if (nextPage === this.currentPage || Number.isNaN(nextPage)) {
      return;
    }

    this.currentPage = nextPage;
    this.renderCards();
    this.scrollResultsIntoView();
  }

  toggleFilters() {
    this.setFiltersOpen(!this.filtersOpen);
  }

  setFiltersOpen(open) {
    this.filtersOpen = Boolean(open);
    this.dataset.filtersOpen = this.filtersOpen ? 'true' : 'false';

    this.filtersToggleLabels.forEach((label) => {
      label.textContent = this.filtersOpen ? this.hideFiltersText : this.showFiltersText;
    });

    this.querySelectorAll('[data-filters-toggle]').forEach((button) => {
      button.setAttribute('aria-expanded', this.filtersOpen ? 'true' : 'false');
    });

    this.setGridScale(this.gridScale);
  }

  setGridScale(scale) {
    const min = Number.parseInt(this.gridScaleSlider?.min || '2', 10);
    const max = Number.parseInt(this.gridScaleSlider?.max || '4', 10);
    const normalizedScale = Math.min(Math.max(Number.parseInt(scale, 10) || 3, min), max);
    const effectiveScale = this.getEffectiveGridScale(normalizedScale);

    this.gridScale = normalizedScale;
    this.dataset.gridScale = String(normalizedScale);

    if (this.gridScaleSlider) {
      this.gridScaleSlider.value = String(normalizedScale);
    }

    if (this.cardsContainer) {
      this.cardsContainer.style.setProperty('--yg-idea-starter-grid-scale', effectiveScale);
    }
  }

  getEffectiveGridScale(scale) {
    if (this.isMobileLayout()) {
      return 2;
    }

    let effectiveScale = Number.parseInt(scale, 10) || 3;

    if (window.innerWidth >= 1000 && !this.filtersOpen) {
      effectiveScale += 1;
    }

    return Math.min(effectiveScale, 5);
  }

  toggleMultiSelectMode(enabled) {
    const shouldEnable = Boolean(enabled) && !this.isMobileLayout();

    this.dataset.multiSelect = shouldEnable ? 'true' : 'false';

    if (this.multiSelectToggle) {
      this.multiSelectToggle.checked = shouldEnable;
    }

    if (shouldEnable) {
      document.body.classList.add('yg-multi-select-active');
    } else {
      document.body.classList.remove('yg-multi-select-active');
      this.clearSelections();
    }

    if (this.floatPanel) {
      if (shouldEnable) {
        this.floatPanel.removeAttribute('hidden');
      } else {
        this.floatPanel.setAttribute('hidden', '');
      }
    }
  }

  toggleDownloadDropdown(toggle) {
    const dropdown = toggle.closest('[data-download-dropdown]');
    const menu = dropdown?.querySelector('[data-download-menu]');
    const willOpen = dropdown?.dataset.open !== 'true';

    this.closeDropdowns(dropdown);

    if (!dropdown || !menu) {
      return;
    }

    dropdown.dataset.open = String(willOpen);
    toggle.setAttribute('aria-expanded', String(willOpen));
    menu.hidden = !willOpen;
  }

  closeDropdowns(exceptDropdown = null) {
    this.querySelectorAll('[data-download-dropdown]').forEach((dropdown) => {
      if (dropdown === exceptDropdown) {
        return;
      }

      const toggle = dropdown.querySelector('[data-download-toggle]');
      const menu = dropdown.querySelector('[data-download-menu]');

      dropdown.dataset.open = 'false';
      toggle?.setAttribute('aria-expanded', 'false');

      if (menu) {
        menu.hidden = true;
      }
    });
  }

  toggleSortDropdown(toggle) {
    const dropdown = toggle.closest('[data-sort-dropdown]');
    const menu = dropdown?.querySelector('[data-sort-menu]');
    const willOpen = dropdown?.dataset.open !== 'true';

    this.closeSortDropdowns(dropdown);

    if (!dropdown || !menu) {
      return;
    }

    dropdown.dataset.open = String(willOpen);
    toggle.setAttribute('aria-expanded', String(willOpen));
    menu.hidden = !willOpen;
  }

  closeSortDropdowns(exceptDropdown = null) {
    this.querySelectorAll('[data-sort-dropdown]').forEach((dropdown) => {
      if (dropdown === exceptDropdown) {
        return;
      }

      const toggle = dropdown.querySelector('[data-sort-toggle]');
      const menu = dropdown.querySelector('[data-sort-menu]');

      dropdown.dataset.open = 'false';
      toggle?.setAttribute('aria-expanded', 'false');

      if (menu) {
        menu.hidden = true;
      }
    });
  }

  setSortMode(value, label) {
    this.sortMode = value || 'relevant';
    this.currentPage = 1;

    this.querySelectorAll('[data-sort-option]').forEach((option) => {
      option.classList.toggle('is-active', option.dataset.sortOption === this.sortMode);
    });

    this.querySelectorAll('[data-sort-label]').forEach((labelElement) => {
      labelElement.textContent = label;
    });

    this.closeSortDropdowns();
    this.applyFilters();
  }

  handleCardCheckboxChange(checkbox) {
    const cardKey = checkbox.dataset.fileKey || '';
    const files = this.parseSelectedCardFiles(checkbox);
    const card = checkbox.closest('.yg-idea-starter-resources__card');

    if (!cardKey || files.length === 0) {
      checkbox.checked = false;
      this.selectedFiles.delete(cardKey);
      this.updateSelectedCounter();
      return;
    }

    if (checkbox.checked) {
      this.selectedFiles.set(cardKey, {
        title: checkbox.dataset.productTitle || '',
        files
      });
    } else {
      this.selectedFiles.delete(cardKey);

      if (this.floatSelectAll) {
        this.floatSelectAll.checked = false;
      }
    }

    card?.classList.toggle('is-selected', checkbox.checked);
    this.updateSelectedCounter();
  }

  selectAllCards(checked) {
    this.querySelectorAll('[data-card-checkbox]').forEach((checkbox) => {
      checkbox.checked = Boolean(checked);
      this.handleCardCheckboxChange(checkbox);
    });
  }

  parseSelectedCardFiles(checkbox) {
    try {
      const files = JSON.parse(checkbox.dataset.cardFiles || '[]');

      return Array.isArray(files) ? files.filter((file) => file?.url) : [];
    } catch (error) {
      return [];
    }
  }

  syncRenderedSelections() {
    this.querySelectorAll('[data-card-checkbox]').forEach((checkbox) => {
      const checked = this.selectedFiles.has(checkbox.dataset.fileKey);

      checkbox.checked = checked;
      checkbox.closest('.yg-idea-starter-resources__card')?.classList.toggle('is-selected', checked);
    });
  }

  updateSelectedCounter() {
    const currentPageCheckboxes = Array.from(this.querySelectorAll('[data-card-checkbox]'));
    const selectedCurrentPageCount = currentPageCheckboxes.filter((checkbox) => checkbox.checked && !checkbox.disabled).length;
    const count = this.selectedFiles.size;

    this.querySelectorAll('[data-selected-count]').forEach((counter) => {
      counter.textContent = `${count}`;
    });

    if (this.floatDownloadButton) {
      this.floatDownloadButton.disabled = count === 0;
    }

    if (this.floatSelectAll) {
      this.floatSelectAll.checked = currentPageCheckboxes.length > 0 && selectedCurrentPageCount >= currentPageCheckboxes.length;
    }
  }

  exitMultiSelectMode() {
    if (this.multiSelectToggle) {
      this.multiSelectToggle.checked = false;
    }

    this.toggleMultiSelectMode(false);
  }

  clearSelections() {
    this.selectedFiles.clear();
    this.querySelectorAll('[data-card-checkbox]').forEach((checkbox) => {
      checkbox.checked = false;
      checkbox.closest('.yg-idea-starter-resources__card')?.classList.remove('is-selected');
    });
    if (this.floatSelectAll) {
      this.floatSelectAll.checked = false;
    }
    this.updateSelectedCounter();
  }

  async handleDownloadLink(event, link) {
    event.preventDefault();
    this.setDownloadLoading(link);

    try {
      if (await this.canDownloadNatively(link)) {
        this.triggerNativeDownload(link);
      } else {
        await this.downloadLinkAsBlob(link);
      }
    } catch (error) {
      console.warn('Falling back to native download link.', error);
      this.triggerNativeDownload(link);
    } finally {
      this.clearDownloadLoading(link);
    }
  }

  setDownloadLoading(trigger) {
    if (!trigger) {
      return;
    }

    trigger.dataset.loading = 'true';
    trigger.setAttribute('aria-busy', 'true');
  }

  clearDownloadLoading(trigger) {
    if (!trigger) {
      return;
    }

    delete trigger.dataset.loading;
    trigger.removeAttribute('aria-busy');
  }

  async canDownloadNatively(link) {
    const href = link.getAttribute('href');

    if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
      return false;
    }

    if (this.isSameOriginDownload(link.href)) {
      return true;
    }

    return this.hasAttachmentDisposition(link.href);
  }

  async hasAttachmentDisposition(url) {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 5000);

    try {
      const response = await fetch(url, {
        method: 'HEAD',
        mode: 'cors',
        credentials: 'omit',
        signal: controller.signal
      });
      const disposition = response.headers.get('content-disposition') || '';

      return /attachment/i.test(disposition);
    } catch (error) {
      return false;
    } finally {
      window.clearTimeout(timeoutId);
    }
  }

  triggerNativeDownload(link) {
    const url = link.href;
    const fileName = link.getAttribute('download') || this.getDownloadNameFromUrl(url);

    if (!url) {
      return;
    }

    if (this.isSameOriginDownload(url)) {
      const temporaryLink = document.createElement('a');

      temporaryLink.href = url;
      temporaryLink.download = fileName;
      temporaryLink.rel = 'noopener';
      temporaryLink.style.display = 'none';
      document.body.appendChild(temporaryLink);
      temporaryLink.click();
      temporaryLink.remove();
      return;
    }

    const iframe = document.createElement('iframe');

    iframe.src = url;
    iframe.hidden = true;
    iframe.setAttribute('aria-hidden', 'true');
    document.body.appendChild(iframe);
    window.setTimeout(() => iframe.remove(), 60000);
  }

  isSameOriginDownload(url) {
    try {
      return new URL(url, window.location.href).origin === window.location.origin;
    } catch (error) {
      return false;
    }
  }

  async downloadLinkAsBlob(link) {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 8000);

    try {
      const response = await fetch(link.href, {
        mode: 'cors',
        credentials: 'omit',
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(`Download request failed with status ${response.status}`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const temporaryLink = document.createElement('a');

      temporaryLink.href = objectUrl;
      temporaryLink.download = link.getAttribute('download') || this.getDownloadNameFromUrl(link.href);
      temporaryLink.style.display = 'none';
      document.body.appendChild(temporaryLink);
      temporaryLink.click();
      temporaryLink.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } finally {
      window.clearTimeout(timeoutId);
    }
  }

  async loadZipLibrary() {
    if (this.zipLibLoaded) {
      return this.zipLib;
    }

    return new Promise((resolve, reject) => {
      if (window.zip && window.zip.ZipWriter) {
        this.zipLib = window.zip;
        this.zipLibLoaded = true;
        resolve(this.zipLib);
        return;
      }

      const existingScript = document.querySelector('script[data-yg-script="zip-js"]');

      if (existingScript) {
        existingScript.addEventListener('load', () => {
          if (window.zip && window.zip.ZipWriter) {
            this.zipLib = window.zip;
            this.zipLibLoaded = true;
            resolve(this.zipLib);
          } else {
            reject(new Error('Zip.js loaded but ZipWriter is missing'));
          }
        }, { once: true });
        existingScript.addEventListener('error', () => reject(new Error('Zip.js script failed to load')), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/@zip.js/zip.js/dist/zip.min.js';
      script.defer = true;
      script.dataset.ygScript = 'zip-js';
      script.onload = () => {
        if (window.zip && window.zip.ZipWriter) {
          this.zipLib = window.zip;
          this.zipLibLoaded = true;
          resolve(this.zipLib);
        } else {
          reject(new Error('Zip.js loaded but ZipWriter is missing'));
        }
      };
      script.onerror = () => reject(new Error('Zip.js script failed to load'));
      document.head.appendChild(script);
    });
  }

  async startBatchDownload() {
    if (this.selectedFiles.size === 0 || !this.floatDownloadButton) {
      return;
    }

    const originalButtonHtml = this.floatDownloadButton.innerHTML;
    this.floatDownloadButton.disabled = true;
    this.floatDownloadButton.innerHTML = '<span>Packing ZIP...</span>';

    try {
      const zipLib = await this.loadZipLibrary();
      const files = [];

      this.selectedFiles.forEach((fileItem) => {
        fileItem.files.forEach((file) => {
          if (file?.url) {
            files.push(file);
          }
        });
      });

      const zipWriter = new zipLib.ZipWriter(new zipLib.BlobWriter('application/zip'));

      for (const file of files) {
        try {
          const response = await fetch(file.url);

          if (!response.ok) {
            throw new Error('Fetch failed');
          }

          const blob = await response.blob();
          await zipWriter.add(this.buildZipEntryName(file), new zipLib.BlobReader(blob));
        } catch (fileError) {
          console.error(`Failed to pack file ${file.title}:`, fileError);
        }
      }

      const zipBlob = await zipWriter.close();
      const downloadUrl = URL.createObjectURL(zipBlob);
      const temporaryLink = document.createElement('a');

      temporaryLink.href = downloadUrl;
      temporaryLink.download = 'eureka-idea-starter-files.zip';
      temporaryLink.style.display = 'none';
      document.body.appendChild(temporaryLink);
      temporaryLink.click();
      temporaryLink.remove();
      window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 5000);
      this.exitMultiSelectMode();
    } catch (error) {
      window.alert(`Batch download packing failed: ${error.message}`);
      console.error(error);
    } finally {
      this.floatDownloadButton.innerHTML = originalButtonHtml;
      this.floatDownloadButton.disabled = this.selectedFiles.size === 0;
    }
  }

  renderLoadingState() {
    if (!this.cardsContainer) {
      return;
    }

    this.cardsContainer.innerHTML = `<div class="yg-idea-starter-resources__loading text-base">${this.escapeHtml(this.loadingText)}</div>`;
    this.paginationContainer.innerHTML = '';
  }

  renderErrorState() {
    if (!this.cardsContainer) {
      return;
    }

    this.cardsContainer.innerHTML = `<div class="yg-idea-starter-resources__empty text-base">${this.escapeHtml(this.errorText)}</div>`;
    this.paginationContainer.innerHTML = '';
  }

  updateResultsCount(count = this.currentCards.length) {
    if (this.resultsCountElement) {
      this.resultsCountElement.textContent = `${count} ${count === 1 ? this.resultText : this.resultsText}`;
    }

    this.querySelectorAll('[data-mobile-filter-results-count]').forEach((element) => {
      element.textContent = String(count);
    });
  }

  scrollResultsIntoView() {
    const top = this.getBoundingClientRect().top + window.scrollY - 80;

    window.scrollTo({
      top: Math.max(top, 0),
      behavior: 'smooth'
    });
  }

  getTotalPages() {
    return Math.max(Math.ceil(this.currentCards.length / this.pageSize), 1);
  }

  getCardKey(card, index) {
    return `${this.extractNumericId(card.id)}-${index}`;
  }

  getCardDownloadFiles(card) {
    return Array.isArray(card?.files) ? card.files.filter((file) => file?.url) : [];
  }

  getUniqueFiles(files) {
    const seen = new Set();

    return (files || []).reduce((result, file) => {
      const key = file?.url || '';

      if (!key || seen.has(key)) {
        return result;
      }

      seen.add(key);
      result.push(file);
      return result;
    }, []);
  }

  fieldsArrayToMap(fields) {
    return (fields || []).reduce((accumulator, field) => {
      accumulator[field.key] = field;
      return accumulator;
    }, {});
  }

  getFileUrl(field) {
    return this.normalizeString(field?.reference?.url) || this.normalizeString(field?.value);
  }

  getFileNameFromUrl(url) {
    return this.getDownloadNameFromUrl(url);
  }

  getDownloadNameFromUrl(url) {
    try {
      const pathname = new URL(url, window.location.href).pathname;
      const fileName = pathname.split('/').filter(Boolean).pop();

      return decodeURIComponent(fileName || 'download');
    } catch (error) {
      return 'download';
    }
  }

  getActualFileType({ fileName = '', url = '', mimeType = '' } = {}) {
    const extension = this.extractFileExtension(fileName || url);

    if (extension) {
      return this.getFileTypeLabel(extension);
    }

    const normalizedMimeType = this.normalizeString(mimeType).toLowerCase();

    if (!normalizedMimeType) {
      return '';
    }

    const mimeSubtype = normalizedMimeType.split('/').pop()?.split(';')[0]?.trim() || '';

    return this.getFileTypeLabel(mimeSubtype);
  }

  getFileTypeLabel(value) {
    const normalizedValue = this.normalizeString(value).toLowerCase();

    if (!normalizedValue) {
      return '';
    }

    const fileTypeMap = {
      avif: 'AVIF',
      bmp: 'BMP',
      gif: 'GIF',
      heic: 'HEIC',
      heif: 'HEIF',
      jpeg: 'JPG',
      jpg: 'JPG',
      json: 'JSON',
      mov: 'MOV',
      mp4: 'MP4',
      m4v: 'M4V',
      ogv: 'OGV',
      pdf: 'PDF',
      png: 'PNG',
      svg: 'SVG',
      tif: 'TIF',
      tiff: 'TIFF',
      txt: 'TXT',
      webm: 'WEBM',
      webp: 'WEBP',
      xls: 'XLS',
      xlsx: 'XLSX',
      zip: 'ZIP'
    };

    return fileTypeMap[normalizedValue] || normalizedValue.toUpperCase();
  }

  extractFileExtension(value) {
    const cleanValue = this.normalizeString(value).split('?')[0].split('#')[0];
    const extension = cleanValue.includes('.') ? cleanValue.split('.').pop() : '';

    return this.normalizeString(extension).toLowerCase();
  }

  buildFileName(title, url, fallbackExtension = '') {
    const extension = this.extractFileExtension(url) || fallbackExtension;
    const slug = this.normalizeString(title)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'download';

    return extension ? `${slug}.${extension}` : slug;
  }

  buildZipEntryName(file) {
    return file.fileName || this.buildFileName(file.title || 'download', file.url);
  }

  getFileSizeBytes(value) {
    const normalizedValue = this.normalizeString(value).toLowerCase();
    const amount = Number.parseFloat(normalizedValue);

    if (Number.isNaN(amount)) {
      return 0;
    }

    if (normalizedValue.includes('gb')) {
      return amount * 1024 * 1024 * 1024;
    }

    if (normalizedValue.includes('mb')) {
      return amount * 1024 * 1024;
    }

    if (normalizedValue.includes('kb')) {
      return amount * 1024;
    }

    return amount;
  }

  extractNumericId(gid) {
    return String(gid || '').split('/').pop();
  }

  normalizeString(value) {
    return String(value || '').trim();
  }

  chunkArray(items, size) {
    const chunks = [];

    for (let index = 0; index < items.length; index += size) {
      chunks.push(items.slice(index, index + size));
    }

    return chunks;
  }

  renderChevronIcon() {
    return `
      <svg class="yg-idea-starter-resources__button-arrow" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
  }

  renderDownloadItemIcon() {
    return `
      <svg class="yg-idea-starter-resources__download-item-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M8 2.5V10" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/>
        <path d="M5 7.5L8 10.5L11 7.5" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M3.5 13H12.5" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/>
      </svg>
    `;
  }

  escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  escapeAttribute(value) {
    return this.escapeHtml(value);
  }

  isMobileLayout() {
    return window.matchMedia('(max-width: 999px)').matches;
  }

  get algoliaAppId() {
    return this.dataset.algoliaAppId || '';
  }

  get algoliaSearchKey() {
    return this.dataset.algoliaSearchKey || '';
  }

  get algoliaIndexName() {
    return this.dataset.algoliaIndexName || 'shopify_pages';
  }

  get algoliaHitsPerPage() {
    return Number.parseInt(this.dataset.algoliaHitsPerPage || '100', 10);
  }

  get storefrontDomain() {
    return this.dataset.storefrontDomain || window.location.host;
  }

  get storefrontToken() {
    return this.dataset.storefrontToken || '';
  }

  get storefrontApiVersion() {
    return this.dataset.storefrontApiVersion || '2024-04';
  }

  get storefrontEndpoint() {
    return `https://${this.storefrontDomain}/api/${this.storefrontApiVersion}/graphql.json`;
  }

  get searchPlaceholder() {
    return this.dataset.searchPlaceholder || 'Search Options';
  }

  get pageSize() {
    return Number.parseInt(this.dataset.pageSize || '12', 10);
  }

  get facetLimit() {
    return Number.parseInt(this.dataset.facetLimit || '10', 10);
  }

  get loadingText() {
    return this.dataset.loadingText || 'Loading idea starters...';
  }

  get noResultsText() {
    return this.dataset.noResultsText || 'No idea starters found.';
  }

  get errorText() {
    return this.dataset.errorText || 'Something went wrong. Please try again later.';
  }

  get noFiltersText() {
    return this.dataset.noFiltersText || 'No filters available.';
  }

  get showMoreText() {
    return this.dataset.showMoreText || 'Show More';
  }

  get showLessText() {
    return this.dataset.showLessText || 'Show Less';
  }

  get hideFiltersText() {
    return this.dataset.hideFiltersText || 'Hide Filters';
  }

  get showFiltersText() {
    return this.dataset.showFiltersText || 'Show Filters';
  }

  get downloadText() {
    return this.dataset.downloadText || 'Download';
  }

  get resultText() {
    return this.dataset.resultText || 'result';
  }

  get resultsText() {
    return this.dataset.resultsText || 'results';
  }
}

if (!window.customElements.get('yg-idea-starter-resources')) {
  window.customElements.define('yg-idea-starter-resources', YGIdeaStarterResources);
}
